import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { STATS } from "../../lib/content";

function useCountUp(target, active) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    const num = parseFloat(String(target).replace(/[^0-9.]/g, ""));
    if (isNaN(num)) return;
    let start = 0;
    const dur = 1400;
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(num * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target]);
  return val;
}

function StatItem({ value, suffix, label, active }) {
  const isNumeric = /^[0-9.,]+$/.test(value.replace(/,/g, ""));
  const counted = useCountUp(value, active && isNumeric);
  const display = isNumeric
    ? value.includes(",")
      ? Math.round(counted).toLocaleString()
      : Number.isInteger(parseFloat(value))
        ? Math.round(counted).toString()
        : counted.toFixed(0)
    : value;

  return (
    <div className="relative px-6 py-4">
      <div className="flex items-baseline gap-2">
        <span className="font-serif-lux text-5xl md:text-7xl gold-gradient-text">
          {display}
        </span>
        {suffix && (
          <span className="text-sm md:text-base tracking-[0.14em] uppercase text-[#d4af37]/80">
            {suffix}
          </span>
        )}
      </div>
      <p className="mt-3 text-sm text-[#9a968c] max-w-[16rem] leading-relaxed">
        {label}
      </p>
    </div>
  );
}

export default function Stats() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      ref={ref}
      data-testid="stats"
      className="relative bg-[#050505] py-20 md:py-28 border-b border-[#d4af37]/10"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-[#d4af37]/12">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.12 }}
            >
              <StatItem {...s} active={inView} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
